/**
 * Ryan O'Dowd
 * 2018-04-15
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  FlatList,
  Platform,
  View,
} from 'react-native';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  fetchChatMessages,
  markAllMessagesRead,
  sendChatMessage,
  setFeedbackChatDraftText,
} from '@utils/store/actions';
import ChatMessage from './ChatMessage';
import EmptyScreen from '@components/common/EmptyScreen';
import EnglishNote from './EnglishNote';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import Icon from '@react-native-vector-icons/material-design-icons';
import {
  KeyboardAvoidingView,
} from 'react-native-keyboard-controller';
import NewVersion from '@components/NewVersion';
import ScreenWrapper from '@containers/ScreenWrapper';
import TextInput from '@components/common/TextInput';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';

const FeedbackChat = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  const draftText = hooks.useTypedSelector((state) => state.feedbackChatDraftText);
  const [_messageText, setMessageText] = useState(draftText);

  hooks.useBackButton(() => {
    dispatch(setFeedbackChatDraftText(_messageText));
    navigation.goBack();
    return true;
  });

  const oakUser = hooks.useTypedSelector((state) => state.oakUser);
  const userSettings = hooks.useTypedSelector((state) => state.userSettings);

  const chats = hooks.useTypedSelector((state: any) => state.chatMessages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) || [], (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue)); // @TODO: clean up (this is either acorn or tibbits/wrinkly): probably want to pass in chats to make this a dumb component

  let textColor = styles.colors.white;
  if (globals.appName !== 'acorn') {
    textColor = styles.isDarkMode ? styles.colors.white : styles.colors.primaryExtraDark;
  }

  useEffect(() => {
    dispatch(markAllMessagesRead());
    dispatch(fetchChatMessages());
  }, [dispatch]);

  const backIcon = (
    <Header.BackIcon
      onPress={() => {
        dispatch(setFeedbackChatDraftText(_messageText));
        navigation.goBack();
      }}
    />
  );

  const _renderMessage = useCallback(({item}) => { // @TODO: remove this wrapper?
    const message = item;
    return (
      <ChatMessage
        message={message}
        senderIsMe={+message.sender_user_id === +oakUser.id}
      />
    );
  }, [oakUser.id]);

  return (
    <ScreenWrapper>
      <Header
        leftItems={[backIcon]}
        title={i18n('feedbackChat')}
      />
      {Platform.OS !== 'web' && <NewVersion isFeedbackChat={true} />}
      {(userSettings.locale_id && userSettings.locale_id !== 1) && <EnglishNote />}
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingContainer}
        behavior='padding'
      >
        <FullScreenScrollView noScroll={true}>
          <View style={styles.chatContainer}>
            <View style={[styles.messagesContainer, !!chats.length && styles.width100]}>
              {(chats.length <= 0 || oakUser.is_feedback_chat_disabled)
                ? (
                  <EmptyScreen.Wrapper>
                    <EmptyScreen.Text>
                      {oakUser.is_feedback_chat_disabled ? i18n('feedbackChatDisabled') : i18n('feedbackEmptyScreen')}
                    </EmptyScreen.Text>
                  </EmptyScreen.Wrapper>
                )
                : (
                  <FlatList
                    inverted
                    contentContainerStyle={styles.flatListMessagesContainer}
                    style={styles.messagesList}
                    data={chats}
                    renderItem={_renderMessage}
                  />
                )
              }
              {!oakUser.is_feedback_chat_disabled &&
                <View style={styles.newMessageContainer}>
                  <TextInput
                    style={styles.newMessage}
                    onChangeText={(messageText) => setMessageText(messageText)}
                    value={_messageText}
                    multiline={true}
                    placeholder={i18n('newMessage')}
                    underlineColorAndroid='transparent'
                    selectionColor={styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primaryDark}
                    textColor={textColor}
                    blurOnSubmit={false}
                    returnKeyType='default'
                    disableFullscreenUI={true}
                    disabled={oakUser.is_feedback_chat_disabled}
                    autoCorrect={true}
                  />
                  <View style={styles.saveMessageIconWrapper}>
                    <Icon
                      style={[styles.saveMessageIcon, (!_messageText || oakUser.is_feedback_chat_disabled) ? styles.saveMessageIconDisabled : {}]}// @TODO: disabled should be a style...make this an icon button with a disabled prop
                      name='check-circle'
                      onPress={() => {
                        dispatch(sendChatMessage(_messageText));
                        setMessageText('');
                        dispatch(setFeedbackChatDraftText(''));
                      }}
                    />
                  </View>
                </View>
              }
            </View>
          </View>
        </FullScreenScrollView>
      </KeyboardAvoidingView>
    </ScreenWrapper>
  );
};

export default FeedbackChat;
